// BOORO: the chats of the sidebar in the order of the organigram. The Chief
// leads the account and stands above every office; each office lists the
// agents that sit in it directly, then its groups. A group or an office with a
// lead lists the lead first, with a crown, and its other agents indented below.
// With one office there is no office level: its agents and groups stand on top.
// The list stays flat, as Rakazo's is.
import { orderedGroups } from "@booro/team";
import type { OrgSnapshot } from "@rakazo/contracts";
import type { sidebarGroupsForSpaces } from "./space-sidebar";

type SidebarChat = {
  id: string;
  name: string;
  title?: string | null;
  preview?: string | null;
  pinned: boolean;
  sectionId: string | null;
};

type SidebarSpace<TBot extends SidebarChat, TGroup extends SidebarChat> = {
  id: string;
  name: string;
  canRename?: boolean;
  canDelete?: boolean;
  bots: readonly TBot[];
  groups: readonly TGroup[];
  botSections: readonly { id: string; name: string }[];
};

export type OrgSidebarSection<TBot extends SidebarChat, TGroup extends SidebarChat> = ReturnType<
  typeof sidebarGroupsForSpaces<TBot, TGroup>
>[number] & {
  /** How deep a group sits in its office; the office itself is 0. */
  depth?: number;
  color?: string | null;
};

/** The key of the section of an office, which lists the agents outside any group. */
export function officeSectionKey(spaceId: string): string {
  return `space:${spaceId}:unassigned`;
}

export const CHIEF_SECTION_KEY = "chief";

export function orgSidebarSections<TBot extends SidebarChat, TGroup extends SidebarChat>(
  spaces: readonly SidebarSpace<TBot, TGroup>[],
  org: Pick<OrgSnapshot, "chiefBotId" | "groups" | "agents"> &
    Partial<Pick<OrgSnapshot, "offices">>,
  query: string,
  collapsed: ReadonlySet<string>,
): OrgSidebarSection<TBot, TGroup>[] {
  const needle = query.toLowerCase();
  const hit = (chat: SidebarChat) =>
    `${chat.name} ${chat.title ?? ""} ${chat.preview ?? ""}`.toLowerCase().includes(needle);
  const placeOf = new Map(org.agents.map((agent) => [agent.id, agent.groupId]));
  const officeLead = new Map((org.offices ?? []).map((office) => [office.id, office.leadBotId]));
  /** Where an agent stands in its group or office: its lead, under a lead, or among equals. */
  type Rank = "lead" | "member" | undefined;
  const botItem = (chat: TBot, sectionId: string | null, rank?: Rank) => ({
    kind: "bot" as const,
    chat,
    id: chat.id,
    pinned: chat.pinned,
    sectionId,
    ...(rank ? { rank } : {}),
  });
  const rankIn = (leadId: string | null | undefined, bot: TBot): Rank =>
    !leadId ? undefined : bot.id === leadId ? "lead" : "member";
  const chatItem = (chat: TGroup) => ({
    kind: "group" as const,
    chat,
    id: chat.id,
    pinned: chat.pinned,
    sectionId: null,
  });
  type Item = ReturnType<typeof botItem> | ReturnType<typeof chatItem>;
  // The lead first, as everyone below hangs from it; then pinned chats, then
  // the order the person gave.
  const rank = (item: Item) => ("rank" in item && item.rank === "lead" ? 0 : item.pinned ? 1 : 2);
  const ordered = (items: Item[]) =>
    items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => rank(a.item) - rank(b.item) || a.index - b.index)
      .map(({ item }) => item);

  const sections: OrgSidebarSection<TBot, TGroup>[] = [];
  const showLock = spaces.length > 1;
  const oneOffice = spaces.length === 1;
  const quiet = {
    subtitle: undefined as string | undefined,
    showLock: false,
    emptySpaceId: undefined as string | undefined,
    canRenameSpace: false,
    canDeleteSpace: false,
    canOpenSpaceSettings: false,
  };

  for (const space of spaces) {
    const chief = space.bots.find((bot) => bot.id === org.chiefBotId);
    if (chief && hit(chief)) {
      sections.unshift({
        ...quiet,
        key: CHIEF_SECTION_KEY,
        title: null,
        bots: [botItem(chief, null)],
        sectionId: null,
        spaceId: space.id,
        spaceName: space.name,
      });
    }
  }

  for (const space of spaces) {
    const bots = space.bots.filter((bot) => bot.id !== org.chiefBotId);
    const groups = orderedGroups(org.groups, space.id);
    const known = new Set(groups.map(({ group }) => group.id));
    const groupOf = (bot: TBot) => {
      const id = placeOf.get(bot.id) ?? null;
      return id && known.has(id) ? id : null;
    };
    const leadHere = bots.find((bot) => bot.id === officeLead.get(space.id) && !groupOf(bot))?.id;
    const loose: Item[] = [
      ...bots
        .filter((bot) => !groupOf(bot) && hit(bot))
        .map((bot) => botItem(bot, null, rankIn(leadHere, bot))),
      ...space.groups.filter(hit).map(chatItem),
    ];
    const members = groups.map(({ group, depth }) => {
      const rows = bots.filter((bot) => groupOf(bot) === group.id && hit(bot));
      const lead = bots.find((bot) => bot.id === group.leadBotId && groupOf(bot) === group.id)?.id;
      return {
        group,
        depth,
        bots: ordered(rows.map((bot) => botItem(bot, group.id, rankIn(lead, bot)))),
      };
    });
    if (needle && loose.length === 0 && members.every((entry) => entry.bots.length === 0)) {
      continue;
    }
    const officeKey = officeSectionKey(space.id);
    const empty = bots.length === 0 && space.groups.length === 0 && groups.length === 0;
    sections.push({
      key: officeKey,
      title: oneOffice ? null : space.name,
      subtitle: undefined,
      bots: ordered(loose),
      sectionId: null,
      showLock,
      // An office without anything opens its start page instead of folding.
      emptySpaceId: empty && showLock ? space.id : undefined,
      spaceId: space.id,
      spaceName: space.name,
      canRenameSpace: space.canRename === true,
      canDeleteSpace: space.canDelete === true,
      canOpenSpaceSettings: !oneOffice,
      depth: 0,
    });
    if (!needle && !oneOffice && collapsed.has(officeKey)) continue;
    // A folded group hides its subgroups too.
    const hidden = new Set<string>();
    for (const { group, depth, bots: rows } of members) {
      if (group.parentGroupId && hidden.has(group.parentGroupId)) {
        hidden.add(group.id);
        continue;
      }
      if (needle && rows.length === 0) continue;
      const key = `space:${space.id}:section:${group.id}`;
      sections.push({
        ...quiet,
        key,
        title: group.name,
        bots: ordered(rows),
        sectionId: group.id,
        spaceId: space.id,
        spaceName: space.name,
        depth: depth + 1,
        color: group.color,
      });
      if (!needle && collapsed.has(key)) hidden.add(group.id);
    }
  }
  return sections;
}
